import { createContext, useContext, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchMemberMe, memberLogout, updateMemberPreferences } from "@/lib/api";
import { useConfig } from "@/hooks/useConfig";
import { getTimezone, setTimezone } from "@/lib/time";
import { getOddsFormat, setOddsFormat } from "@/lib/odds";

const AuthContext = createContext(null);

// Member identity comes from the Whop session cookie (httpOnly, set by the backend).
// VIP access = verified Whop membership, or the admin "VIP Test Mode" for development previews.
export function AuthProvider({ children }) {
  const qc = useQueryClient();
  const { vipTestMode, config } = useConfig();
  const { data, isLoading } = useQuery({
    queryKey: ["member-me"],
    queryFn: fetchMemberMe,
    retry: false,
    staleTime: 60_000,
  });

  const member = data || null;
  const isVip = !!member?.vip || vipTestMode === "vip";

  // Signed-in members: their saved account preferences win over device defaults.
  useEffect(() => {
    const p = member?.preferences;
    if (!p) return;
    if (p.timezone && p.timezone !== getTimezone()) setTimezone(p.timezone);
    if (p.odds_format && p.odds_format !== getOddsFormat()) setOddsFormat(p.odds_format);
  }, [member?.preferences]);

  const savePreferences = useCallback(async (prefs) => {
    if (prefs.timezone !== undefined) setTimezone(prefs.timezone || "");
    if (prefs.odds_format !== undefined) setOddsFormat(prefs.odds_format || "");
    if (member) {
      const res = await updateMemberPreferences({ timezone: prefs.timezone || getTimezone(), odds_format: prefs.odds_format || getOddsFormat() });
      qc.setQueryData(["member-me"], (old) => (old ? { ...old, preferences: res.preferences } : old));
    }
  }, [member, qc]);

  const logout = useCallback(async () => {
    try {
      await memberLogout();
    } finally {
      qc.setQueryData(["member-me"], null);
      qc.invalidateQueries();
    }
  }, [qc]);

  const value = {
    user: member?.user || null,
    isMember: !!member,
    isVip,
    isOwner: member?.role === "owner",
    vipTestMode,
    membershipStatus: member?.membership_status || null,
    membershipPlan: member?.membership_plan || null,
    membershipRenewsAt: member?.membership_renews_at || null,
    membershipCancelAtPeriodEnd: !!member?.membership_cancel_at_period_end,
    discordInviteUrl: member?.discord_invite_url || null,
    loginEnabled: config ? !!config.whop?.login_enabled : true,
    configLoaded: !!config,
    checking: isLoading,
    logout,
    savePreferences,
    preferences: member?.preferences || {},
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
